import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CodeSnippetView from '../../CodeEditor/Shared/CodeSnippetView';
import BasicSnackBar from '../../Snackbars/BasicSnackBar';

interface PreviewPythonDialogProps {
  onClose: () => void;
  open: boolean;
  pythonSource: string;
}

const PreviewPythonDialog = ({ onClose, open, pythonSource }: PreviewPythonDialogProps) => {
  const { t } = useTranslation();
  const [copiedOpen, setCopiedOpen] = useState(false);

  const fallbackCopyToClipboard = () => {
    const textArea = document.createElement('textarea');
    textArea.value = pythonSource;
    textArea.style.position = 'fixed';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      return document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Copy command failed', err);
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const copyToClipboard = async () => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(pythonSource);
        setCopiedOpen(true);
        return;
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }

    if (fallbackCopyToClipboard()) {
      setCopiedOpen(true);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle variant="dialogTitle" align="center">
          <Trans i18nKey="previewPythonDialog.title">Preview Python</Trans>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3, px: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body1" component="p">
              <Trans i18nKey="previewPythonDialog.body">
                This code is read-only. Copy it if you need it, or close this window when you are done.
              </Trans>
            </Typography>
            <Stack
              sx={{
                maxHeight: 420,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                backgroundColor: 'background.default',
              }}
            >
              <CodeSnippetView title="preview-python-source" value={pythonSource} />
            </Stack>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ justifyContent: 'center', px: 2, py: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="text" startIcon={<ContentCopyIcon />} onClick={() => void copyToClipboard()}>
              <Trans i18nKey="previewPythonDialog.copy">Copy</Trans>
            </Button>
            <Button variant="contained" onClick={onClose}>
              <Trans i18nKey="button.close">Close</Trans>
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
      <BasicSnackBar
        message={t('previewPythonDialog.copied', 'Copied to Clipboard')}
        open={copiedOpen}
        onClose={setCopiedOpen}
        vertical="bottom"
        horizontal="center"
      />
    </>
  );
};

export default PreviewPythonDialog;
